import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { BrandMark, ThemeToggle } from '../components/ui';
import { GoogleButton } from '../components/GoogleButton';
import { ApiError } from '../lib/api';

export function LoginPage() {
  const { branding } = useTenant();
  const { requestEmailOtp, verifyEmailOtp, loginGoogle } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCode = async () => {
    setError(null); setBusy(true);
    try { await requestEmailOtp(email.trim()); setStep('code'); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not send the code. Try again.'); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setError(null); setBusy(true);
    try { await verifyEmailOtp(email.trim(), code.trim()); navigate('/'); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'That code did not work. Check and retry.'); }
    finally { setBusy(false); }
  };

  const google = async (idToken: string) => {
    setError(null); setBusy(true);
    try { await loginGoogle(idToken); navigate('/'); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Google sign-in failed. Try again.'); }
    finally { setBusy(false); }
  };

  return (
    /* ⚠ The layout was an INLINE `gridTemplateColumns: '1fr 1fr'` with no media query anywhere.
       An inline style CANNOT be overridden by a breakpoint — that was the whole bug. On a phone it
       produced two 190px columns. Now it's a class, and it collapses to one column under 860px. */
    <div className="lg-wrap">
      {/* left: brand panel. HIDDEN on mobile — squashing it into 190px helped nobody. */}
      <div className="lg-brand">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
          <BrandMark size={40} />
          <span className="h-display" style={{ fontSize: 20 }}>{branding?.name ?? 'Eduvora'}</span>
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 14 }}>School administration</div>
          <h1 className="h-display lg-brand-title">
            Everything the office runs on, in one calm place.
          </h1>
          <p className="lg-brand-lead">
            Fees, students, and receipts — with a clear record of every rupee.
          </p>
        </div>
        {/* ambient accent arc */}
        <div aria-hidden style={{
          position: 'absolute', right: -120, bottom: -120, width: 360, height: 360,
          borderRadius: '50%', border: '1px solid var(--accent)', opacity: .35,
        }} />
        <div aria-hidden style={{
          position: 'absolute', right: -60, bottom: -60, width: 240, height: 240,
          borderRadius: '50%', border: '1px solid var(--accent)', opacity: .25,
        }} />
      </div>

      {/* right: form */}
      <div className="lg-form-side">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><ThemeToggle /></div>

        <div className="lg-form">
          {/* Shown ONLY on mobile, where the brand panel is hidden. Without it a phone user sees a
              bare form and no idea whose school they're signing in to. */}
          <div className="lg-mobile-brand">
            <BrandMark size={32} />
            <strong style={{ fontSize: 17, fontFamily: 'var(--font-display)' }}>
              {branding?.name ?? 'Eduvora'}
            </strong>
          </div>

          <h2 className="h-display" style={{ fontSize: 26, marginBottom: 6 }}>
            {step === 'email' ? 'Sign in' : 'Enter your code'}
          </h2>
          <p style={{ color: 'var(--ink-2)', marginBottom: 24, fontSize: 14 }}>
            {step === 'email'
              ? 'We’ll email you a one-time code.'
              : <>Sent to <strong>{email}</strong>. It expires in 10 minutes.</>}
          </p>

          {error && (
            <div className="pill pill-danger" style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 16, padding: '8px 12px', borderRadius: 'var(--r-md)' }}>
              {error}
            </div>
          )}

          {step === 'email' ? (
            <>
              <label className="label" htmlFor="email">Email</label>
              <input id="email" className="input" type="email" placeholder="you@institution.edu"
                value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && email && sendCode()} autoFocus />
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}
                disabled={!email || busy} onClick={sendCode}>
                {busy ? 'Sending…' : 'Send code'}
              </button>
              <GoogleButton onCredential={google} />
            </>
          ) : (
            <>
              <label className="label" htmlFor="code">6-digit code</label>
              <input id="code" className="input mono" inputMode="numeric" maxLength={6}
                placeholder="000000" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && verify()}
                autoFocus style={{ letterSpacing: '.3em', fontSize: 18, textAlign: 'center' }} />
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}
                disabled={code.length !== 6 || busy} onClick={verify}>
                {busy ? 'Verifying…' : 'Sign in'}
              </button>
              <button className="btn" style={{ width: '100%', justifyContent: 'center', marginTop: 10, border: 'none' }}
                onClick={() => { setStep('email'); setCode(''); setError(null); }}>
                Use a different email
              </button>
            </>
          )}

          <p style={{ marginTop: 24, fontSize: 12, color: 'var(--ink-3)', textAlign: 'center' }}>
            Trouble signing in? Contact {branding?.supportEmail ?? 'your administrator'}.
          </p>
        </div>
      </div>
    </div>
  );
}
